/**
 * EzParcel Native Services
 * Wrapper for Capacitor native plugins
 * Provides offline storage, push notifications, GPS tracking
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Geolocation } from '@capacitor/geolocation';
import { Network } from '@capacitor/network';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Preferences } from '@capacitor/preferences';

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

// ============================================
// PUSH NOTIFICATIONS
// ============================================

export const PushNotificationService = {
  async initialize() {
    if (!isNative()) {
      console.log('Push notifications require native platform');
      return false;
    }

    try {
      // Request permission
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        // Register with FCM
        await PushNotifications.register();
        
        // Listen for registration
        PushNotifications.addListener('registration', (token) => {
          console.log('Push registration token:', token.value);
          // TODO: Send token to backend for targeting
          this.saveToken(token.value);
        });

        // Listen for notifications received while app is in foreground
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push notification received:', notification);
          // Show local notification
          this.showLocalNotification(notification.title, notification.body);
        });

        // Listen for notification actions
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Push notification action:', notification);
          // Handle navigation based on notification data
        });

        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Push notification initialization error:', error);
      return false;
    }
  },

  async saveToken(token) {
    await Preferences.set({ key: 'fcm_token', value: token });
  },

  async getToken() {
    const { value } = await Preferences.get({ key: 'fcm_token' });
    return value;
  },

  async showLocalNotification(title, body, data = {}) {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Date.now(),
            title,
            body,
            extra: data,
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'default',
            smallIcon: 'ic_stat_icon',
            iconColor: '#E53935'
          }
        ]
      });
    } catch (error) {
      console.error('Local notification error:', error);
    }
  }
};

// ============================================
// GEOLOCATION / GPS TRACKING
// ============================================

export const GPSService = {
  watchId: null,
  callbacks: [],

  async getCurrentPosition() {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000
      });
      
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        speed: position.coords.speed,
        heading: position.coords.heading,
        timestamp: position.timestamp
      };
    } catch (error) {
      console.error('GPS error:', error);
      throw error;
    }
  },

  async startTracking(callback) {
    try {
      // Request permissions first
      const permission = await Geolocation.requestPermissions();
      
      if (permission.location === 'granted' || permission.coarseLocation === 'granted') {
        this.callbacks.push(callback);
        
        if (!this.watchId) {
          this.watchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true },
            (position, error) => {
              if (error) {
                console.error('GPS watch error:', error);
                return;
              }
              
              const locationData = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                speed: position.coords.speed,
                heading: position.coords.heading,
                timestamp: position.timestamp
              };
              
              // Notify all callbacks
              this.callbacks.forEach(cb => cb(locationData));
              
              // Save to offline storage
              this.saveLocation(locationData);
            }
          );
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('GPS tracking error:', error);
      return false;
    }
  },

  async stopTracking() {
    if (this.watchId) {
      await Geolocation.clearWatch({ id: this.watchId });
      this.watchId = null;
      this.callbacks = [];
    }
  },

  async saveLocation(location) {
    try {
      const { value } = await Preferences.get({ key: 'gps_history' });
      const history = value ? JSON.parse(value) : [];
      history.push(location);
      
      // Keep only last 1000 points
      if (history.length > 1000) {
        history.shift();
      }
      
      await Preferences.set({ key: 'gps_history', value: JSON.stringify(history) });
    } catch (error) {
      console.error('GPS save error:', error);
    }
  },

  async getLocationHistory() {
    try {
      const { value } = await Preferences.get({ key: 'gps_history' });
      return value ? JSON.parse(value) : [];
    } catch (error) {
      return [];
    }
  }
};

// ============================================
// OFFLINE STORAGE
// ============================================

export const OfflineStorage = {
  async set(key, value) {
    try {
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      await Preferences.set({ key, value: data });
      return true;
    } catch (error) {
      console.error('Storage set error:', error);
      return false;
    }
  },

  async get(key) {
    try {
      const { value } = await Preferences.get({ key });
      if (!value) return null;
      
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Storage get error:', error);
      return null;
    }
  },

  async remove(key) {
    try {
      await Preferences.remove({ key });
      return true;
    } catch (error) {
      console.error('Storage remove error:', error);
      return false;
    }
  },

  async clear() {
    try {
      await Preferences.clear();
      return true;
    } catch (error) {
      console.error('Storage clear error:', error);
      return false;
    }
  },

  // Queue offline actions for sync
  async queueAction(action) {
    const queue = await this.get('offline_queue') || [];
    queue.push({
      ...action,
      queuedAt: new Date().toISOString()
    });
    await this.set('offline_queue', queue);
  },

  async getQueue() {
    return await this.get('offline_queue') || [];
  },

  async clearQueue() {
    await this.remove('offline_queue');
  },

  async syncQueue(syncFunction) {
    const queue = await this.getQueue();
    const results = [];
    
    for (const action of queue) {
      try {
        await syncFunction(action);
        results.push({ action, success: true });
      } catch (error) {
        results.push({ action, success: false, error });
      }
    }
    
    // Clear successfully synced items
    const failedActions = results.filter(r => !r.success).map(r => r.action);
    await this.set('offline_queue', failedActions);
    
    return results;
  }
};

// ============================================
// NETWORK STATUS
// ============================================

export const NetworkService = {
  callbacks: [],
  isConnected: true,

  async initialize() {
    try {
      const status = await Network.getStatus();
      this.isConnected = status.connected;
      
      Network.addListener('networkStatusChange', (status) => {
        this.isConnected = status.connected;
        this.callbacks.forEach(cb => cb(status.connected));
        
        // Auto-sync when coming back online
        if (status.connected) {
          console.log('Network connected - syncing offline queue');
        }
      });
      
      return status.connected;
    } catch (error) {
      console.error('Network init error:', error);
      return true;
    }
  },

  onStatusChange(callback) {
    this.callbacks.push(callback);
  },

  getStatus() {
    return this.isConnected;
  }
};

// ============================================
// HAPTIC FEEDBACK
// ============================================

export const HapticService = {
  async impact(style = 'medium') {
    if (!isNative()) return;
    
    try {
      const impactStyle = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy
      }[style] || ImpactStyle.Medium;
      
      await Haptics.impact({ style: impactStyle });
    } catch (error) {
      // Haptics not available
    }
  },

  async notification(type = 'success') {
    if (!isNative()) return;
    
    try {
      await Haptics.notification({ type });
    } catch (error) {
      // Haptics not available
    }
  },

  async vibrate() {
    if (!isNative()) return;
    
    try {
      await Haptics.vibrate();
    } catch (error) {
      // Vibration not available
    }
  }
};

// ============================================
// INITIALIZE ALL SERVICES
// ============================================

export const initializeNativeServices = async () => {
  if (!isNative()) {
    console.log('Running in web mode - native services disabled');
    return;
  }
  
  console.log('Initializing native services for:', getPlatform());
  
  // Initialize network monitoring
  await NetworkService.initialize();
  
  // Initialize push notifications
  await PushNotificationService.initialize();
  
  console.log('Native services initialized');
};

export default {
  isNative,
  getPlatform,
  PushNotificationService,
  GPSService,
  OfflineStorage,
  NetworkService,
  HapticService,
  initializeNativeServices
};
